import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ForbiddenError, UnauthorizedError, fetchScans as realFetch, type FetchScans } from './api';
import { firebaseAuth, type AuthService, type AuthUser } from './auth';
import { PlayerList } from './PlayerList';
import { ScanMap, type MapFocus } from './ScanMap';
import { defaultRange, groupByCell, playerRange, summary, validateRange } from './scans';
import type { Scan, ScansResponse } from './types';

interface Props {
  auth?: AuthService;
  fetchScans?: FetchScans;
  /** The current time; replaceable so tests are not tied to the clock. */
  now?: () => Date;
}

type Load =
  | { status: 'loading' }
  | { status: 'ok'; data: ScansResponse }
  | { status: 'error'; message: string }
  | { status: 'forbidden' };

/** The player the page is filtered to. `label` is the name if known, else the id. */
interface PlayerFilter {
  id: string;
  label: string;
}

const clock = () => new Date();

export function App({ auth = firebaseAuth, fetchScans = realFetch, now = clock }: Props) {
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => auth.onChange(setUser, (e) => setAuthError(e.message)), [auth]);

  const signIn = () => {
    setAuthError(null);
    auth.signInWithGoogle().catch((e: Error) => setAuthError(e.message));
  };

  if (user === undefined) return <p className="center">Loading…</p>;
  if (user === null) {
    return (
      <main className="center signin">
        <h1>EthrHunt Admin</h1>
        <button type="button" className="primary" onClick={signIn}>
          Sign in with Google
        </button>
        {authError && <p role="alert" className="error">{authError}</p>}
      </main>
    );
  }
  return <Admin user={user} auth={auth} fetchScans={fetchScans} now={now} />;
}

function Admin({ user, auth, fetchScans, now }: { user: AuthUser; auth: AuthService; fetchScans: FetchScans; now: () => Date }) {
  const initial = useMemo(() => defaultRange(now()), [now]);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [rangeEdited, setRangeEdited] = useState(false);
  const [player, setPlayer] = useState<PlayerFilter | null>(null);
  const [playerText, setPlayerText] = useState('');
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [reload, setReload] = useState(0);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const request = useRef(0);

  const rangeError = validateRange(from, to, player !== null);

  useEffect(() => {
    if (rangeError) return;
    const id = ++request.current;
    setLoad({ status: 'loading' });
    (async () => {
      try {
        const token = await auth.idToken();
        const data = await fetchScans({ from, to, playerId: player?.id }, token);
        if (id === request.current) setLoad({ status: 'ok', data });
      } catch (e) {
        if (id !== request.current) return;
        if (e instanceof UnauthorizedError) {
          await auth.signOut();
        } else if (e instanceof ForbiddenError) {
          setLoad({ status: 'forbidden' });
        } else {
          setLoad({ status: 'error', message: (e as Error).message || 'Something went wrong' });
        }
      }
    })();
  }, [auth, fetchScans, from, to, player, reload, rangeError]);

  const pickPlayer = useCallback(
    (id: string, label: string) => {
      const trimmed = id.trim();
      if (!trimmed) return;
      setPlayer({ id: trimmed, label });
      setPlayerText(trimmed);
      if (!rangeEdited) {
        const r = playerRange(now());
        setFrom(r.from);
        setTo(r.to);
      }
    },
    [rangeEdited, now],
  );

  const clearPlayer = () => {
    setPlayer(null);
    setPlayerText('');
    setRangeEdited(false);
    const r = defaultRange(now());
    setFrom(r.from);
    setTo(r.to);
  };

  const applyPlayerText = () => {
    const t = playerText.trim();
    if (!t) {
      if (player) clearPlayer();
      return;
    }
    if (t !== player?.id) pickPlayer(t, t);
  };

  const scans = useMemo(() => (load.status === 'ok' ? load.data.scans : []), [load]);
  const groups = useMemo(() => groupByCell(scans), [scans]);
  const stats = summary(scans);
  const label = player ? (scans.find((s) => s.playerId === player.id)?.playerName ?? player.label) : null;

  if (load.status === 'forbidden') {
    return (
      <main className="center">
        <h1>Not authorised</h1>
        <p>Your user id is not on the admin list. Add this id to <code>ADMIN_USER_IDS</code> and redeploy:</p>
        <p className="uid" data-testid="uid">{user.uid}</p>
        <button type="button" onClick={() => void auth.signOut()}>Sign out</button>
      </main>
    );
  }

  const selectScan = (scan: Scan) => setFocus({ cellId: scan.cellId, nonce: (focus?.nonce ?? 0) + 1 });

  return (
    <div className="shell">
      <header className="bar">
        <h1>EthrHunt Admin</h1>
        <label>
          From
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setRangeEdited(true); }} />
        </label>
        <label>
          To
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setRangeEdited(true); }} />
        </label>
        <label>
          Player
          <input
            type="text"
            placeholder="player id"
            value={playerText}
            onChange={(e) => setPlayerText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyPlayerText(); }}
            onBlur={applyPlayerText}
          />
        </label>
        {player && (
          <span className="filter">
            Showing {label}
            <button type="button" onClick={clearPlayer}>Clear</button>
          </span>
        )}
        {load.status === 'ok' && (
          <span className="summary">
            {stats.scans} scans, {stats.multiPart} multi-part, {stats.locations} locations
          </span>
        )}
        <button type="button" className="signout" onClick={() => void auth.signOut()}>Sign out</button>
      </header>
      {rangeError && <p role="alert" className="notice error">{rangeError}</p>}
      {load.status === 'ok' && load.data.truncated && (
        <p role="status" className="notice warn">Only the newest 5,000 scans are shown. Choose a shorter range to see the rest.</p>
      )}
      {load.status === 'loading' && !rangeError && <p role="status" className="notice">Loading…</p>}
      {load.status === 'error' && (
        <p role="alert" className="notice error">
          {load.message} <button type="button" onClick={() => setReload((n) => n + 1)}>Retry</button>
        </p>
      )}
      <div className="stage">
        <ScanMap groups={groups} onPickPlayer={pickPlayer} focus={focus} />
        {load.status === 'ok' && scans.length === 0 && (
          <p className="empty">{player ? 'No scans for this player in this range' : 'No scans in this range'}</p>
        )}
      </div>
      {player && load.status === 'ok' && scans.length > 0 && <PlayerList scans={scans} onSelect={selectScan} />}
    </div>
  );
}
